import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, ChevronRight, Search, Plus, Grid3x3, List,
  MapPin, Calendar, DollarSign, Gavel, Users, MessageSquare,
  FileText, BadgeCheck, ClipboardList, Send, Mail, Sparkles, MoreHorizontal,
  Hammer, Eye, Pencil, Upload, AlertCircle, Check, CheckCircle2, ShieldCheck,
  Briefcase, Building2, Wand2, Wrench, Zap, Wind, Layers, PaintBucket, Cog,
  Truck, Lock, ExternalLink, Edit3, GripVertical, ArrowUpRight, Banknote,
} from 'lucide-react';

/**
 * Project Hub Phase 2 — FINAL VISUAL PREVIEW
 *
 * Three views in one page (switch with the segmented control at top):
 *   1. Pipeline — tile grid + global views + funded badge
 *   2. Inside a project — drill-down with Bid Sheet tab + transfer wizard
 *   3. Bid form & Schedule template — contractor duration breakdown + 8-phase
 *      schedule editor (master template) — both wired to the auto-fill flow.
 *
 * No backend. Static demo. User picks "approved" to start the actual build.
 */

// =================================================================
// DEMO DATA
// =================================================================

const PIPELINE_PROJECTS = [
  {
    id: 'p1', name: 'Mantua Pikesville', address: '1615 38th St, Baltimore MD',
    type: 'New build · 1 unit', stage: 'funded',
    target_budget: 612000, low_total: 591200,
    bids_in: 18, scopes_total: 12, scopes_quoted: 11, awaiting_bids: 1,
    bid_sheet_status: 'approved', last_activity: '2h ago', accent: '#10b981',
  },
  {
    id: 'p2', name: 'Bolton Hill Rehab', address: '208 Bolton St, Baltimore MD',
    type: 'Rehab + addition', stage: 'submitted',
    target_budget: 285000, low_total: 268500,
    bids_in: 14, scopes_total: 9, scopes_quoted: 9, awaiting_bids: 0,
    bid_sheet_status: 'submitted', confirmed: 8, confirm_total: 13,
    last_activity: 'Yesterday', accent: '#3b82f6',
  },
  {
    id: 'p3', name: 'Hampden Townhomes', address: '3402 Falls Rd, Baltimore MD',
    type: 'New build · 4 units', stage: 'sheet_ready',
    target_budget: 1240000, low_total: 1182400,
    bids_in: 22, scopes_total: 14, scopes_quoted: 14, awaiting_bids: 0,
    bid_sheet_status: 'draft', last_activity: '20m ago', accent: '#8b5cf6',
  },
  {
    id: 'p4', name: '38th & Greenmount', address: '601 E 38th St, Baltimore MD',
    type: 'Rehab', stage: 'collecting',
    target_budget: 165000, low_total: 47800,
    bids_in: 4, scopes_total: 7, scopes_quoted: 3, awaiting_bids: 4,
    bid_sheet_status: null, last_activity: '3 days ago', accent: '#f59e0b',
  },
  {
    id: 'p5', name: 'Druid Hill Flip', address: '1812 Druid Hill Ave', type: 'Rehab',
    stage: 'walked', target_budget: 195000, low_total: 0,
    bids_in: 0, scopes_total: 6, scopes_quoted: 0, awaiting_bids: 0,
    bid_sheet_status: null, last_activity: '2 weeks ago', accent: '#64748b',
  },
];

const STAGES = {
  collecting:  { label: 'Collecting bids',  tone: 'bg-amber-50 text-amber-800 border-amber-200',  dot: '#f59e0b' },
  sheet_ready: { label: 'Bid sheet ready',  tone: 'bg-purple-50 text-purple-800 border-purple-200',dot: '#8b5cf6' },
  submitted:   { label: 'Submitted to bank',tone: 'bg-blue-50 text-blue-800 border-blue-200',     dot: '#3b82f6' },
  funded:      { label: 'Funded',           tone: 'bg-emerald-50 text-emerald-800 border-emerald-200', dot: '#10b981' },
  walked:      { label: 'Walked / archived',tone: 'bg-slate-100 text-slate-600 border-slate-200', dot: '#64748b' },
};

// 8-phase template — matches CONSTRUCTION_PHASE_DEFINITIONS in server.py
const TEMPLATE_PHASES = [
  { n: 1, name: 'Planning & Permitting',        icon: ClipboardList, est_days: 30, scopes: ['Architect', 'Engineer', 'Permits'] },
  { n: 2, name: 'Site Prep & Foundation',       icon: Truck,         est_days: 18, scopes: ['Excavation', 'Footings', 'Foundation walls', 'Water/sewer taps', 'Slab'] },
  { n: 3, name: 'Framing',                      icon: Hammer,        est_days: 24, scopes: ['Framing materials', 'Framing labor', 'Sheathing'] },
  { n: 4, name: 'Exterior & Systems (Rough-in)',icon: Layers,        est_days: 35, scopes: ['Roofing', 'Windows + Doors', 'Siding', 'Plumbing rough', 'Electrical rough', 'HVAC rough'] },
  { n: 5, name: 'Insulation & Drywall',         icon: Layers,        est_days: 14, scopes: ['Insulation', 'Drywall hang', 'Drywall finish'] },
  { n: 6, name: 'Interior Finishes',            icon: PaintBucket,   est_days: 28, scopes: ['Flooring', 'Cabinets', 'Trim + Doors', 'Paint', 'Tile'] },
  { n: 7, name: 'Fixtures & Exterior Finish',   icon: Cog,           est_days: 12, scopes: ['Plumbing trim', 'Electrical trim', 'HVAC trim', 'Appliances', 'Landscaping', 'Driveway'] },
  { n: 8, name: 'Final Walkthrough & CO',       icon: BadgeCheck,    est_days: 5,  scopes: ['Punch list', 'Cleaning', 'Final inspections', 'CO'] },
];

// Trade → duration breakdown map (hard-coded, option A confirmed)
const TRADE_BREAKDOWNS = {
  plumbing:   { icon: Wrench, label: 'Plumbing',   stages: ['Ground rough-in', 'Wall rough-in', 'Trim-out'] },
  electrical: { icon: Zap,    label: 'Electrical', stages: ['Rough-in', 'Trim-out'] },
  hvac:       { icon: Wind,   label: 'HVAC',       stages: ['Rough-in', 'Trim-out'] },
  drywall:    { icon: Layers, label: 'Drywall',    stages: ['Hang', 'Finish'] },
  framing:    { icon: Hammer, label: 'Framing',    stages: null },
  excavation: { icon: Truck,  label: 'Excavation', stages: null },
  siding:     { icon: Layers, label: 'Siding',     stages: null },
  painting:   { icon: PaintBucket, label: 'Painting', stages: ['Prime', 'Finish coat'] },
};

// =================================================================
// VIEW 1 — PIPELINE
// =================================================================

const PipelineView = ({ onOpen }) => (
  <div>
    {/* Global views row */}
    <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-5 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[11px] uppercase tracking-wide bg-slate-900 text-white px-2 py-0.5 rounded font-semibold">Global views</span>
        <span className="text-xs text-slate-500">— everything across ALL your deals</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {[
          { icon: Gavel, label: 'Compare all bids', count: 'LOWEST', tone: 'bg-emerald-50 border-emerald-200 hover:border-emerald-400', accent: 'text-emerald-700', badge: 'bg-green-600 text-white' },
          { icon: Users, label: 'All contractors', count: '42', tone: 'bg-blue-50 border-blue-200 hover:border-blue-400', accent: 'text-blue-700', badge: 'bg-slate-100 text-slate-700' },
          { icon: FileText, label: 'All documents', count: '238', tone: 'bg-purple-50 border-purple-200 hover:border-purple-400', accent: 'text-purple-700', badge: 'bg-slate-100 text-slate-700' },
        ].map((g, i) => {
          const Icon = g.icon;
          return (
            <button key={i} className={`text-left ${g.tone} border rounded-xl p-3 transition-colors`}>
              <div className="flex items-center gap-2 mb-1">
                <Icon className={`w-4 h-4 ${g.accent}`} />
                <span className="text-sm font-semibold text-slate-900">{g.label}</span>
                <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded font-semibold ${g.badge}`}>{g.count}</span>
              </div>
              <div className="text-xs text-slate-600">
                {i === 0 && 'Side-by-side trade comparison · cheapest bid highlighted.'}
                {i === 1 && 'Full roster · W-9 / COI / 1099 status across every project.'}
                {i === 2 && 'Every receipt, waiver, COI in one searchable feed.'}
              </div>
            </button>
          );
        })}
      </div>
    </div>

    {/* Pipeline header */}
    <div className="flex items-center gap-2 mb-3 flex-wrap">
      <span className="text-[11px] uppercase tracking-wide bg-slate-900 text-white px-2 py-0.5 rounded font-semibold">Deal pipeline</span>
      <span className="text-xs text-slate-500">— prospective deals collecting bids · funded deals get a green badge · click a tile to drill in</span>
    </div>

    {/* Toolbar */}
    <div className="flex flex-wrap items-center gap-3 mb-5">
      <div className="flex-1 max-w-md relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          placeholder="Search deals by address or contractor…"
          className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
        />
      </div>
      <select className="text-xs border border-slate-200 rounded-lg px-2 py-2 bg-white">
        <option>All stages</option>
        <option>Collecting bids</option>
        <option>Bid sheet ready</option>
        <option>Submitted to bank</option>
        <option>Funded</option>
        <option>Walked / archived</option>
      </select>
      <button className="bg-blue-600 text-white text-sm font-semibold px-4 py-2 rounded-lg inline-flex items-center gap-1.5 hover:bg-blue-700">
        <Plus className="w-4 h-4" /> New deal
      </button>
    </div>

    {/* Tiles */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {PIPELINE_PROJECTS.map(p => {
        const st = STAGES[p.stage];
        const bidPct = Math.round((p.scopes_quoted / Math.max(p.scopes_total, 1)) * 100);
        const isFunded = p.stage === 'funded';
        const isWalked = p.stage === 'walked';
        return (
          <button
            key={p.id}
            onClick={() => onOpen(p)}
            className={`text-left bg-white rounded-2xl border ${isFunded ? 'border-emerald-300' : isWalked ? 'border-slate-200 opacity-70' : 'border-slate-200 hover:border-blue-300'} hover:shadow-lg shadow-sm transition-all overflow-hidden group relative`}
          >
            {/* Funded badge */}
            {isFunded && (
              <div className="absolute top-3 right-3 z-10 bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full inline-flex items-center gap-1">
                <BadgeCheck className="w-3 h-3" /> Funded
              </div>
            )}
            <div style={{ height: 6, background: p.accent }} />
            <div className="p-4">
              <div className="flex items-start justify-between mb-1 gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-base font-semibold text-slate-900 truncate group-hover:text-blue-700 pr-12">{p.name}</div>
                  <div className="text-xs text-slate-500 inline-flex items-center gap-1 mt-0.5">
                    <MapPin className="w-3 h-3" /> {p.address}
                  </div>
                </div>
              </div>
              <div className="mt-2 text-xs text-slate-600 flex items-center gap-2 flex-wrap">
                <span className={`px-2 py-0.5 rounded inline-flex items-center gap-1 ${st.tone} border`}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: st.dot }} />
                  {st.label}
                </span>
                {p.confirmed != null && (
                  <span className="bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 rounded inline-flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                    {p.confirmed} of {p.confirm_total} confirmed
                  </span>
                )}
                <span className="text-slate-400">·</span>
                <span>{p.type}</span>
              </div>

              {/* Bid completeness bar */}
              <div className="mt-3">
                <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
                  <span>Bids in</span>
                  <span>{p.scopes_quoted} of {p.scopes_total} scopes quoted</span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${bidPct}%`, background: p.accent }} />
                </div>
              </div>

              {/* Totals row */}
              <div className="mt-3 text-[11px] grid grid-cols-2 gap-2">
                <div>
                  <div className="text-slate-500">Target budget</div>
                  <div className="font-semibold text-slate-900">${p.target_budget.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-slate-500">Lowest-bid total</div>
                  <div className={`font-semibold ${p.low_total < p.target_budget ? 'text-emerald-700' : 'text-amber-700'}`}>
                    ${p.low_total.toLocaleString()}
                    {p.low_total > 0 && p.low_total < p.target_budget && <span className="ml-1 text-[10px]">↓${(p.target_budget - p.low_total).toLocaleString()}</span>}
                  </div>
                </div>
              </div>

              {/* Activity badges */}
              <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-600 flex-wrap">
                {p.awaiting_bids > 0 && (
                  <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">
                    <Gavel className="w-3 h-3" /> {p.awaiting_bids} awaiting bids
                  </span>
                )}
                {p.bids_in > 0 && (
                  <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
                    {p.bids_in} bids received
                  </span>
                )}
                <span className="text-slate-400 ml-auto">{p.last_activity}</span>
              </div>

              {/* Funded action row */}
              {isFunded && (
                <div className="mt-3 pt-3 border-t border-emerald-100 flex items-center justify-between text-xs">
                  <span className="text-emerald-700 font-semibold inline-flex items-center gap-1">
                    <ArrowUpRight className="w-3.5 h-3.5" /> Transferred to active project
                  </span>
                  <span className="text-blue-700 underline">Go to active build</span>
                </div>
              )}
            </div>
          </button>
        );
      })}
      <button className="rounded-2xl border-2 border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50 transition-colors flex flex-col items-center justify-center text-slate-500 hover:text-blue-700 p-8">
        <Plus className="w-6 h-6 mb-2" />
        <div className="text-sm font-semibold">New deal</div>
        <div className="text-xs">Start collecting bids on a property</div>
      </button>
    </div>
  </div>
);

// =================================================================
// VIEW 2 — INSIDE A PROJECT (Bolton Hill Rehab, stage: submitted)
// =================================================================

const InsideProjectView = ({ onBack, onOpenTransfer }) => {
  const project = PIPELINE_PROJECTS[1]; // Bolton Hill — submitted
  const [tab, setTab] = useState('bidsheet');
  const st = STAGES[project.stage];
  const tabs = [
    { id: 'scopes',     label: 'Scopes',         icon: ClipboardList, count: 9 },
    { id: 'bids',       label: 'Bid requests',   icon: Gavel,         count: 14 },
    { id: 'bidsheet',   label: 'Bid Sheet',      icon: Briefcase,     count: null, highlight: true },
    { id: 'contractors',label: 'Bidding contractors', icon: Users,    count: 7 },
    { id: 'files',      label: 'Property files', icon: FileText,      count: 12 },
    { id: 'messages',   label: 'Messages',       icon: MessageSquare, count: 3 },
  ];

  return (
    <div>
      <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
        <button onClick={onBack} className="hover:text-slate-900 inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Pipeline
        </button>
        <ChevronRight className="w-3 h-3 text-slate-400" />
        <span className="text-slate-900 font-medium">{project.name}</span>
      </div>

      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-4">
        <div style={{ height: 6, background: project.accent }} />
        <div className="p-5 flex flex-wrap gap-4 items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">{project.name}</h2>
            <div className="text-sm text-slate-500 inline-flex items-center gap-2 mt-1 flex-wrap">
              <MapPin className="w-3.5 h-3.5" /> {project.address}
              <span className={`px-2 py-0.5 rounded ${st.tone} border inline-flex items-center gap-1`}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: st.dot }} />
                {st.label}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Stat label="Target budget" value={`$${project.target_budget.toLocaleString()}`} />
            <Stat label="Lowest-bid total" value={`$${project.low_total.toLocaleString()}`} tone="emerald" />
            <Stat label="Bids received" value={`${project.bids_in}`} tone="blue" />
            <Stat label="Scopes" value={`${project.scopes_quoted}/${project.scopes_total}`} tone="amber" />
          </div>
        </div>
        <div className="border-t border-slate-100 px-3 flex items-center gap-1 overflow-x-auto">
          {tabs.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`text-sm inline-flex items-center gap-1.5 px-3 py-2.5 border-b-2 transition-colors whitespace-nowrap ${active ? 'border-blue-600 text-blue-700 font-semibold' : t.highlight ? 'border-transparent text-emerald-700 font-semibold' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
              >
                <Icon className="w-4 h-4" /> {t.label}
                {t.count != null && t.count > 0 && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${active ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>{t.count}</span>
                )}
                {t.highlight && !active && <Sparkles className="w-3 h-3" />}
              </button>
            );
          })}
        </div>
      </div>

      {tab === 'bidsheet' && <BidSheetTab onOpenTransfer={onOpenTransfer} />}
      {tab !== 'bidsheet' && (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-500 text-sm">
          {tabs.find(t => t.id === tab)?.label} content — mockup focused on the Bid Sheet tab. Click <strong>Bid Sheet</strong> above.
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, tone }) => {
  const tones = { emerald: 'text-emerald-700', blue: 'text-blue-700', amber: 'text-amber-700' };
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
      <div className={`text-lg font-bold ${tones[tone] || 'text-slate-900'}`}>{value}</div>
    </div>
  );
};

// Bid Sheet tab
const BID_SHEET_LINES = [
  { trade: 'Excavation',          contractor: 'Premier Excavators',   amount: 8200,  stages: null,                           phase: 2 },
  { trade: 'Foundation',          contractor: 'Charm City Concrete',  amount: 22400, stages: null,                           phase: 2 },
  { trade: 'Framing',             contractor: 'Block 7 Contracting',  amount: 35800, stages: null,                           phase: 3 },
  { trade: 'Plumbing',            contractor: 'East Side Plumbing',   amount: 19500, stages: { ground: 3, rough: 22, trim: 12 }, phase: 4 },
  { trade: 'Electrical',          contractor: 'Mid-Atlantic Electric',amount: 14200, stages: { rough: 18, trim: 8 },        phase: 4 },
  { trade: 'HVAC',                contractor: 'Hampden HVAC',          amount: 22800, stages: { rough: 15, trim: 6 },        phase: 4 },
  { trade: 'Roofing',             contractor: 'Roof Right MD',         amount: 18500, stages: null,                           phase: 4 },
  { trade: 'Siding',              contractor: 'Atlantic Siding Co.',   amount: 14900, stages: null,                           phase: 4 },
  { trade: 'Drywall',             contractor: 'Mason Drywall',         amount: 31500, stages: { hang: 9, finish: 7 },        phase: 5 },
  { trade: 'Painting',            contractor: 'Bright Coat LLC',       amount: 9800,  stages: { prime: 4, finish: 6 },       phase: 6 },
  { trade: 'Flooring',            contractor: 'Hampden Flooring',      amount: 16800, stages: null,                           phase: 6 },
  { trade: 'Cabinets',            contractor: 'Custom Cabinets MD',    amount: 28000, stages: null,                           phase: 6 },
  { trade: 'Appliances',          contractor: 'Direct Appliance',      amount: 6700,  stages: null,                           phase: 7 },
];

const BidSheetTab = ({ onOpenTransfer }) => {
  const total = BID_SHEET_LINES.reduce((a, l) => a + l.amount, 0);
  const submitted = true; // Bolton Hill is submitted
  return (
    <div className="space-y-4">
      {/* Bank-submission strip */}
      <div className={`rounded-2xl p-5 flex items-center gap-3 flex-wrap ${submitted ? 'bg-emerald-50 border border-emerald-200' : 'bg-blue-50 border border-blue-200'}`}>
        <div className={`p-2 rounded-lg text-white ${submitted ? 'bg-emerald-600' : 'bg-blue-600'}`}>
          {submitted ? <CheckCircle2 className="w-5 h-5" /> : <Banknote className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900">
            {submitted ? 'Submitted to First National (finance department)' : 'Ready to submit'}
          </div>
          <div className="text-xs text-slate-600 mt-0.5">
            {submitted
              ? '13 awarded scopes · $249,100 total · awaiting bank approval — once approved, click "Transfer to active project" below.'
              : 'Review awarded scopes below. Submit packaged bid sheet to bank when ready.'}
          </div>
        </div>
        <button
          onClick={onOpenTransfer}
          className="text-sm bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1.5"
        >
          <Wand2 className="w-4 h-4" /> Bank approved? · Transfer to active project
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Bid Sheet — awarded scopes</h3>
            <p className="text-xs text-slate-500 mt-0.5">Pulled from winning bids. Phase column is auto-mapped from each trade — confirm during transfer.</p>
          </div>
          <div className="flex items-center gap-2">
            <button className="text-xs bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg inline-flex items-center gap-1">
              <FileText className="w-3.5 h-3.5" /> Export PDF
            </button>
            <button className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg inline-flex items-center gap-1">
              <Send className="w-3.5 h-3.5" /> Email to bank
            </button>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2">Trade</th>
              <th className="text-left px-4 py-2">Awarded contractor</th>
              <th className="text-left px-4 py-2">Duration</th>
              <th className="text-left px-4 py-2">Maps to phase</th>
              <th className="text-right px-4 py-2">Amount</th>
            </tr>
          </thead>
          <tbody>
            {BID_SHEET_LINES.map((line, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="px-4 py-2 font-medium text-slate-900">{line.trade}</td>
                <td className="px-4 py-2 text-slate-700">{line.contractor}</td>
                <td className="px-4 py-2 text-xs text-slate-600">
                  {line.stages ? (
                    <div className="space-y-0.5">
                      {Object.entries(line.stages).map(([k, v]) => (
                        <div key={k}>{k}: {v}d</div>
                      ))}
                    </div>
                  ) : '—'}
                </td>
                <td className="px-4 py-2 text-xs text-slate-600">
                  <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">Phase {line.phase}</span>
                </td>
                <td className="px-4 py-2 text-right font-semibold text-slate-900">${line.amount.toLocaleString()}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-slate-200 bg-slate-50">
              <td colSpan={4} className="px-4 py-3 text-right font-bold text-slate-900">Total</td>
              <td className="px-4 py-3 text-right font-bold text-lg text-slate-900">${total.toLocaleString()}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

// =================================================================
// VIEW 3 — TRANSFER WIZARD
// =================================================================

const TransferWizard = ({ onClose }) => {
  const [step, setStep] = useState(1);
  const [kickoff, setKickoff] = useState('2026-03-15');
  const STEP_LABELS = ['Kickoff date', 'Phase mapping', 'Schedule preview', 'Send for confirmation', 'Track & finalize'];
  return (
    <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-50 flex items-start justify-center p-6 overflow-auto" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-4">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-emerald-600" />
            <div>
              <h2 className="text-base font-semibold text-slate-900">Transfer to active project</h2>
              <p className="text-xs text-slate-500">Bolton Hill Rehab — bank approved · 13 awarded scopes → 8 phases · cascade confirmation included</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>

        {/* Steps */}
        <div className="px-6 pt-4 flex items-center gap-2 text-xs flex-wrap">
          {STEP_LABELS.map((s, i) => (
            <React.Fragment key={s}>
              <span className={`px-2 py-1 rounded-full font-semibold ${i + 1 === step ? 'bg-emerald-600 text-white' : i + 1 < step ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                {i + 1 < step ? <Check className="w-3 h-3 inline mr-1" /> : ''}{i + 1}. {s}
              </span>
              {i < STEP_LABELS.length - 1 && <ChevronRight className="w-3 h-3 text-slate-400" />}
            </React.Fragment>
          ))}
        </div>

        <div className="p-6">
          {step === 1 && (
            <div>
              <label className="text-sm font-semibold text-slate-900">When does construction start?</label>
              <p className="text-xs text-slate-500 mt-1 mb-3">Phase 1 begins this day. Every subsequent phase starts where the previous ends, using each contractor&apos;s quoted duration.</p>
              <input type="date" value={kickoff} onChange={(e) => setKickoff(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 inline-flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> Projected completion: <strong>10/30/2026</strong> · about 7.5 months · based on cascading durations from each awarded bid.
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-1">Auto-mapped phase assignments</h3>
              <p className="text-xs text-slate-500 mb-3">Each awarded scope was placed into one of your 8 phases using the trade → phase keyword map. Review and adjust if needed.</p>
              <div className="space-y-1.5 max-h-80 overflow-y-auto">
                {BID_SHEET_LINES.map((line, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded-lg p-2">
                    <span className="font-semibold text-slate-900 w-36 truncate">{line.trade}</span>
                    <span className="text-slate-600 flex-1 truncate">{line.contractor}</span>
                    <ArrowRight className="w-3 h-3 text-slate-400" />
                    <select defaultValue={line.phase} className="text-xs border border-slate-200 rounded px-1 py-0.5 bg-white">
                      {TEMPLATE_PHASES.map(p => <option key={p.n} value={p.n}>Phase {p.n}: {p.name}</option>)}
                    </select>
                    <span className="font-semibold text-slate-900 ml-1 w-20 text-right">${line.amount.toLocaleString()}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 inline-flex items-start gap-2">
                <Sparkles className="w-4 h-4 mt-0.5" />
                <div>
                  <strong>AI suggestion:</strong> 6 trades will run in parallel within Phase 4 (Rough-ins). Phase 4 length = max of those contractors&apos; durations ≈ <strong>37 days</strong>.
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Schedule preview — cascading dates per phase</h3>
              <div className="space-y-1.5 max-h-80 overflow-y-auto">
                {TEMPLATE_PHASES.map(p => {
                  const Icon = p.icon;
                  return (
                    <div key={p.n} className="bg-white border border-slate-200 rounded-lg p-3 flex items-center gap-3">
                      <div className="bg-blue-100 text-blue-700 rounded-lg p-2"><Icon className="w-4 h-4" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900">Phase {p.n}: {p.name}</div>
                        <div className="text-[11px] text-slate-500">{p.scopes.join(' · ')}</div>
                      </div>
                      <div className="text-xs text-slate-700 text-right">
                        <div className="font-semibold">{p.est_days} days</div>
                        <div className="text-[11px] text-slate-500">03/{15 + Math.min(p.n * 2, 28)} → 04/{Math.min(15 + p.n * 5, 28)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-1">Send schedule to all 13 contractors for confirmation</h3>
              <p className="text-xs text-slate-500 mb-3">Uses your existing cascade infrastructure. One rolled-up email per contractor with their proposed window + one-click Confirm / Propose change / Can&apos;t do it buttons (HMAC-signed — no login needed).</p>

              {/* Email preview */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">Preview · what East Side Plumbing receives</div>
                <div className="bg-white border border-slate-200 rounded-lg p-4 text-sm">
                  <div className="text-xs text-slate-500 mb-2 inline-flex items-center gap-1"><Mail className="w-3 h-3" /> &quot;Bolton Hill Rehab — please confirm your schedule&quot;</div>
                  <div className="text-slate-900 leading-relaxed">
                    Hi East Side Plumbing,<br /><br />
                    You&apos;ve been awarded <strong>Plumbing</strong> on Bolton Hill Rehab. Here&apos;s your proposed window:
                  </div>
                  <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                    <div className="font-semibold text-blue-900 inline-flex items-center gap-1.5"><Wrench className="w-4 h-4" /> Plumbing — Phase 4</div>
                    <div className="text-xs text-blue-800 mt-1 space-y-0.5">
                      <div>Ground rough-in: 04/02 → 04/05 (3 days)</div>
                      <div>Wall rough-in: 04/15 → 05/07 (22 days)</div>
                      <div>Trim-out: 09/10 → 09/22 (12 days)</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="bg-emerald-600 text-white text-xs font-semibold px-3 py-1.5 rounded inline-flex items-center gap-1"><Check className="w-3 h-3" /> Confirms this works</span>
                    <span className="bg-amber-500 text-white text-xs font-semibold px-3 py-1.5 rounded inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> Propose different dates</span>
                    <span className="bg-red-500 text-white text-xs font-semibold px-3 py-1.5 rounded inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Can&apos;t do it</span>
                  </div>
                </div>
              </div>

              <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900 inline-flex items-start gap-2">
                <Sparkles className="w-4 h-4 mt-0.5" />
                <div>
                  <strong>Cascade test mode is ON</strong> — first send routes all 13 emails to your test inbox so you can verify before the real send. Toggle off in Settings to email contractors directly.
                </div>
              </div>

              <div className="mt-4 flex items-center gap-2 text-xs">
                <Mail className="w-4 h-4 text-slate-500" />
                <span className="text-slate-600">13 contractors will receive their schedule confirmation request.</span>
              </div>
            </div>
          )}

          {step === 5 && (
            <div>
              <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">Schedule confirmation — live tracker</h3>
                  <p className="text-xs text-slate-500 mt-0.5">8 of 13 contractors have responded so far. Walk away anytime — responses keep coming in and you'll get bell notifications.</p>
                </div>
                <label className="text-xs inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 cursor-pointer">
                  <input type="checkbox" defaultChecked className="rounded" />
                  <span className="text-slate-700"><strong>Auto-finalize</strong> once all confirmed</span>
                </label>
              </div>

              {/* Status summary */}
              <div className="grid grid-cols-4 gap-2 mb-3 text-center text-xs">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2"><div className="font-bold text-emerald-700 text-lg">7</div><div className="text-emerald-800">Confirmed</div></div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-2"><div className="font-bold text-amber-700 text-lg">1</div><div className="text-amber-800">Proposed change</div></div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-2"><div className="font-bold text-red-700 text-lg">0</div><div className="text-red-800">Can&apos;t do it</div></div>
                <div className="bg-slate-100 border border-slate-200 rounded-lg p-2"><div className="font-bold text-slate-700 text-lg">5</div><div className="text-slate-800">Pending</div></div>
              </div>

              {/* List */}
              <div className="space-y-1.5 max-h-72 overflow-y-auto">
                {[
                  { trade: 'Excavation', contractor: 'Premier Excavators', state: 'confirmed', when: 'confirmed 03/29 → 04/01' },
                  { trade: 'Foundation', contractor: 'Charm City Concrete', state: 'confirmed', when: 'confirmed 04/01 → 04/05' },
                  { trade: 'Framing', contractor: 'Block 7 Contracting', state: 'confirmed', when: 'confirmed 04/02 → 04/26' },
                  { trade: 'Plumbing', contractor: 'East Side Plumbing', state: 'pending', when: 'awaiting · sent 2h ago' },
                  { trade: 'Electrical', contractor: 'Mid-Atlantic Electric', state: 'pending', when: 'awaiting · sent 2h ago' },
                  { trade: 'HVAC', contractor: 'Hampden HVAC', state: 'proposed', when: 'proposes 04/30 start · cascades +3 days' },
                  { trade: 'Roofing', contractor: 'Roof Right MD', state: 'confirmed', when: 'confirmed' },
                  { trade: 'Siding', contractor: 'Atlantic Siding Co.', state: 'confirmed', when: 'confirmed' },
                  { trade: 'Drywall', contractor: 'Mason Drywall', state: 'pending', when: 'awaiting' },
                  { trade: 'Painting', contractor: 'Bright Coat LLC', state: 'confirmed', when: 'confirmed' },
                  { trade: 'Flooring', contractor: 'Hampden Flooring', state: 'pending', when: 'awaiting' },
                  { trade: 'Cabinets', contractor: 'Custom Cabinets MD', state: 'pending', when: 'awaiting' },
                  { trade: 'Appliances', contractor: 'Direct Appliance', state: 'confirmed', when: 'confirmed' },
                ].map((r, i) => {
                  const tones = {
                    confirmed: { bg: 'bg-emerald-50 border-emerald-200', icon: <Check className="w-3.5 h-3.5 text-emerald-600" />, color: 'text-emerald-800' },
                    pending:   { bg: 'bg-slate-50 border-slate-200',     icon: <span className="w-3.5 h-3.5 rounded-full bg-slate-300 inline-block" />, color: 'text-slate-700' },
                    proposed:  { bg: 'bg-amber-50 border-amber-200',     icon: <AlertCircle className="w-3.5 h-3.5 text-amber-600" />, color: 'text-amber-900' },
                    declined:  { bg: 'bg-red-50 border-red-200',          icon: <AlertCircle className="w-3.5 h-3.5 text-red-600" />, color: 'text-red-800' },
                  }[r.state];
                  return (
                    <div key={i} className={`${tones.bg} border rounded-lg p-2 text-xs flex items-center gap-2`}>
                      {tones.icon}
                      <span className={`font-semibold w-32 truncate ${tones.color}`}>{r.trade}</span>
                      <span className="text-slate-700 flex-1 truncate">{r.contractor}</span>
                      <span className={`${tones.color} truncate`}>{r.when}</span>
                      {r.state === 'proposed' && <button className="text-amber-700 hover:text-amber-900 underline">Review</button>}
                      {r.state === 'pending' && <button className="text-slate-500 hover:text-slate-700 underline">Resend</button>}
                    </div>
                  );
                })}
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded-lg inline-flex items-center gap-1">
                  <Mail className="w-3.5 h-3.5" /> Resend pending (5)
                </button>
                <button className="text-xs bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-lg inline-flex items-center gap-1">
                  Finalize anyway · accept HVAC delay
                </button>
              </div>

              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 inline-flex items-start gap-2">
                <Sparkles className="w-4 h-4 mt-0.5" />
                <div>
                  <strong>Waiting on responses?</strong> Click <strong>Save &amp; exit</strong> below — the deal stays at "Submitted to bank · 8 of 13 confirmed" on your pipeline tile.
                  Bell notifications ping you when each remaining contractor responds. Click the tile any time to return right here.
                </div>
              </div>

              <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900 inline-flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 mt-0.5" />
                <div>
                  <strong>Finalize</strong> spawns the Active Project, locks in confirmed dates, stamps the <strong>Funded</strong> badge, and emails each contractor a final &ldquo;You&apos;re confirmed — see you on [start date]&rdquo; message.
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-between gap-2 flex-wrap">
          <button onClick={() => setStep(s => Math.max(1, s - 1))} disabled={step === 1} className="text-sm text-slate-600 hover:text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed">Back</button>
          <div className="flex items-center gap-2">
            {step === 5 && (
              <button onClick={onClose} className="text-sm bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold px-4 py-2 rounded-lg inline-flex items-center gap-1.5">
                💾 Save &amp; exit · come back anytime
              </button>
            )}
            {step < STEP_LABELS.length ? (
              <button onClick={() => setStep(s => s + 1)} className={`text-sm font-semibold px-4 py-2 rounded-lg inline-flex items-center gap-1.5 ${step === 4 ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}>
                {step === 4 ? <><Send className="w-4 h-4" /> Send to 13 contractors</> : <>Continue <ArrowRight className="w-4 h-4" /></>}
              </button>
            ) : (
              <button onClick={onClose} className="text-sm bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 py-2 rounded-lg inline-flex items-center gap-1.5">
                <Wand2 className="w-4 h-4" /> Finalize · create active project
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// =================================================================
// VIEW 4 — BID FORM + SCHEDULE TEMPLATE
// =================================================================

const BidFormAndScheduleView = () => {
  const [trade, setTrade] = useState('plumbing');
  const breakdown = TRADE_BREAKDOWNS[trade];
  const BreakIcon = breakdown.icon;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      {/* LEFT — Contractor bid form */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Contractor view</div>
          <h3 className="text-base font-semibold text-slate-900 mt-0.5">Bid form with auto-detected duration breakdown</h3>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-700">Trade you&apos;re bidding on</label>
            <select value={trade} onChange={(e) => setTrade(e.target.value)} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
              {Object.entries(TRADE_BREAKDOWNS).map(([k, t]) => (
                <option key={k} value={k}>{t.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700">Total bid amount</label>
            <div className="mt-1 relative">
              <span className="absolute left-3 top-2 text-slate-500 text-sm">$</span>
              <input type="number" defaultValue={19500} className="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-lg text-sm" />
            </div>
          </div>

          {/* Auto-shown breakdown when trade has sub-stages */}
          {breakdown.stages ? (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-2">
                <BreakIcon className="w-4 h-4 text-blue-700" />
                <span className="text-xs font-semibold text-blue-900">Auto-detected breakdown for {breakdown.label}</span>
                <Sparkles className="w-3 h-3 text-blue-700 ml-auto" />
              </div>
              <p className="text-[11px] text-blue-800 mb-2">Tell us how many days each stage takes. We use this to slot you into the project schedule.</p>
              <div className="space-y-2">
                {breakdown.stages.map((s, i) => (
                  <div key={s} className="flex items-center gap-2 text-xs">
                    <span className="bg-white text-blue-700 border border-blue-200 px-2 py-0.5 rounded font-semibold w-32">{s}</span>
                    <input type="number" defaultValue={[3, 22, 12][i] || 5} className="w-20 border border-blue-200 rounded px-2 py-1 text-sm" />
                    <span className="text-blue-700">days</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1">
                <BreakIcon className="w-4 h-4 text-slate-600" />
                <span className="text-xs font-semibold text-slate-800">{breakdown.label} — single duration</span>
              </div>
              <div className="flex items-center gap-2 text-xs mt-1">
                <input type="number" defaultValue={12} className="w-20 border border-slate-200 rounded px-2 py-1 text-sm" />
                <span className="text-slate-700">days from start to finish</span>
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-semibold text-slate-700">Earliest you can start</label>
            <input type="date" defaultValue="2026-03-15" className="mt-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          </div>

          <button className="w-full mt-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg inline-flex items-center justify-center gap-1.5">
            <Send className="w-4 h-4" /> Submit bid
          </button>
        </div>
      </div>

      {/* RIGHT — 8-phase template editor */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Your settings</div>
            <h3 className="text-base font-semibold text-slate-900 mt-0.5">Master 8-phase template — edit once, reuse forever</h3>
          </div>
          <button className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg inline-flex items-center gap-1">
            <Edit3 className="w-3.5 h-3.5" /> Edit
          </button>
        </div>
        <div className="p-3 space-y-1.5 max-h-[560px] overflow-y-auto">
          {TEMPLATE_PHASES.map(p => {
            const Icon = p.icon;
            return (
              <div key={p.n} className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 flex items-center gap-2">
                <GripVertical className="w-3 h-3 text-slate-400 flex-shrink-0" />
                <div className="bg-blue-100 text-blue-700 rounded p-1.5"><Icon className="w-3.5 h-3.5" /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-slate-900">Phase {p.n}: {p.name}</div>
                  <div className="text-[10px] text-slate-500 truncate">{p.scopes.join(' · ')}</div>
                </div>
                <span className="text-[10px] bg-white border border-slate-200 px-1.5 py-0.5 rounded text-slate-600">{p.est_days}d typical</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// =================================================================
// PAGE
// =================================================================

export default function ProjectHubPhase2Preview() {
  const [view, setView] = useState('pipeline'); // pipeline | inside | bidform
  const [transferOpen, setTransferOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-5">
          <Link to="/" className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 mb-3">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="text-[11px] uppercase tracking-wide bg-purple-100 text-purple-800 px-2 py-1 rounded font-semibold">Mockup · final</span>
            <span className="text-[11px] uppercase tracking-wide bg-amber-100 text-amber-800 px-2 py-1 rounded font-semibold">Not wired yet</span>
            <span className="text-[11px] uppercase tracking-wide bg-emerald-100 text-emerald-800 px-2 py-1 rounded font-semibold">Big-bang ship</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900">Project Hub Phase 2 — full preview</h1>
          <p className="text-slate-600 mt-2 max-w-3xl">
            Pre-construction <strong>deal pipeline</strong> + <strong>Bid Sheet → bank submission</strong> + <strong>one-click Transfer wizard</strong> that
            auto-fills the existing 8-phase active project from awarded bids + duration breakdowns.
            Switch the views below to walk through each piece.
          </p>
        </div>

        {/* View switcher */}
        <div className="bg-white border border-slate-200 rounded-2xl p-2 shadow-sm inline-flex gap-1 mb-6 flex-wrap">
          {[
            { id: 'pipeline', label: '1. Pipeline + Funded badge', icon: Grid3x3 },
            { id: 'inside',   label: '2. Inside a deal · Bid Sheet', icon: Briefcase },
            { id: 'wizard',   label: '3. Transfer wizard', icon: Wand2 },
            { id: 'bidform',  label: '4. Bid form + 8-phase template', icon: Edit3 },
          ].map(v => {
            const Icon = v.icon;
            const active = view === v.id || (view === 'inside' && v.id === 'wizard' && transferOpen);
            return (
              <button
                key={v.id}
                onClick={() => {
                  if (v.id === 'wizard') { setView('inside'); setTransferOpen(true); }
                  else { setView(v.id); setTransferOpen(false); }
                }}
                className={`text-xs font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1.5 transition-colors ${active ? 'bg-blue-600 text-white' : 'text-slate-700 hover:bg-slate-100'}`}
              >
                <Icon className="w-3.5 h-3.5" /> {v.label}
              </button>
            );
          })}
        </div>

        {/* Active view */}
        <div className="bg-white border-2 border-dashed border-slate-200 rounded-3xl p-5 shadow-sm">
          {view === 'pipeline' && <PipelineView onOpen={() => setView('inside')} />}
          {view === 'inside'   && <InsideProjectView onBack={() => setView('pipeline')} onOpenTransfer={() => setTransferOpen(true)} />}
          {view === 'bidform'  && <BidFormAndScheduleView />}
        </div>

        {transferOpen && <TransferWizard onClose={() => setTransferOpen(false)} />}

        {/* What ships */}
        <div className="mt-8 bg-emerald-50 border border-emerald-200 rounded-2xl p-5">
          <h3 className="text-base font-semibold text-emerald-900 mb-2">What ships in the big-bang release (Phase 2 + auto-fill)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-emerald-900">
            <div>
              <div className="font-semibold mb-1">🆕 New flows</div>
              <ul className="space-y-1 list-disc pl-5">
                <li><strong>Pipeline tile grid</strong> at <code>/project-hub</code> · search + stage filter · funded badge on every transferred deal</li>
                <li><strong>Drill-down</strong> at <code>/project-hub/:projectId</code> · bookmarkable · breadcrumb back</li>
                <li><strong>Bid Sheet tab</strong> · awarded scopes roll-up · Export PDF · Email to bank</li>
                <li><strong>Transfer wizard</strong> — 4 steps: kickoff date → phase mapping → schedule preview → confirm</li>
                <li><strong>Contractor bid form</strong> auto-shows the hard-coded duration breakdown when trade has stages (plumbing / electrical / HVAC / drywall / painting)</li>
                <li><strong>8-phase master template editor</strong> · reorder / edit / save as default</li>
                <li><strong>AI sanity layer</strong> — flags date conflicts + ambiguous trade-to-phase mappings · always user-confirmed</li>
                <li><strong>v2.6 What&apos;s New modal</strong> auto-fires on next login</li>
              </ul>
            </div>
            <div>
              <div className="font-semibold mb-1">✅ Kept (zero deletions)</div>
              <ul className="space-y-1 list-disc pl-5">
                <li><strong>Compare all bids</strong> with LOWEST/cheapest highlighting — unchanged</li>
                <li><strong>All Contractors</strong> global page — full roster + W-9/COI/1099 status</li>
                <li><strong>All Documents</strong> global page</li>
                <li><strong>Existing 8-phase</strong> active project structure (this is what the wizard targets)</li>
                <li><strong>New Draw Request</strong> button on active projects — unchanged</li>
                <li>Existing flat-list URL preserved as <code>/project-hub-legacy</code> as a fallback</li>
                <li>Feature flag <code>project_hub_phase2_full</code> — ON for you first, OFF for everyone else until you sign off</li>
                <li>All data 100% preserved · big-bang ship → tested end-to-end before flipping flag globally</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center text-xs text-slate-400">
          Mockup only · click views above · reply &quot;approved&quot; in chat to start the build
        </div>
      </div>
    </div>
  );
}
